const LEVEL_DETAILS = {
  none: { label: 'None', subtitle: 'No reasoning' },
  minimal: { label: 'Minimal', subtitle: 'Fastest reasoning' },
  low: { label: 'Low', subtitle: 'Quick thinking' },
  medium: { label: 'Medium', subtitle: 'Balanced thinking' },
  high: { label: 'High', subtitle: 'Deep thinking' },
  xhigh: { label: 'Extra high', subtitle: 'Extended reasoning' },
  max: { label: 'Max', subtitle: 'Maximum reasoning' },
};

const toOptions = (levels) => levels
  .map((value) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    const details = LEVEL_DETAILS[normalizedValue];
    return details ? { value: normalizedValue, ...details } : null;
  })
  .filter(Boolean);

const OPENAI_FIVE_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];
const OPENAI_FOUR_LEVELS = ['low', 'medium', 'high', 'xhigh'];
const OPENAI_GPT5_LEVELS = ['minimal', 'low', 'medium', 'high'];
const CLAUDE_FIVE_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];
const CLAUDE_FOUR_LEVELS = ['low', 'medium', 'high', 'max'];
const GEMINI_FOUR_LEVELS = ['minimal', 'low', 'medium', 'high'];
const THREE_LEVELS = ['low', 'medium', 'high'];

/**
 * Returns only the effort values accepted by the selected provider model.
 * A Firestore `reasoningLevels` array takes precedence so the catalog can
 * override this compatibility fallback without a frontend release.
 */
export const getReasoningLevelOptions = (model = {}) => {
  model = model || {};

  if (Array.isArray(model.reasoningLevels)) {
    return toOptions(model.reasoningLevels);
  }

  if (!model.isReasoning && model.thinking !== true) return [];

  const modelId = String(model.modelString || '').trim().toLowerCase();

  if (/^gpt-6-astra(?:$|-)/.test(modelId)) return toOptions(OPENAI_FIVE_LEVELS);
  if (/^gpt-5\.6(?:$|-)/.test(modelId)) return toOptions(OPENAI_FIVE_LEVELS);
  if (/^gpt-5\.(?:2|4|5)(?:$|-)/.test(modelId)) return toOptions(OPENAI_FOUR_LEVELS);
  if (/^gpt-5(?:$|-(?:mini|nano)(?:$|-)|-\d{4})/.test(modelId)) {
    return toOptions(OPENAI_GPT5_LEVELS);
  }

  if (
    /^claude-(?:fable-(?:5|5-1)|opus-(?:4-7|4-8|5)|sonnet-5)(?:$|-)/.test(modelId)
  ) {
    return toOptions(CLAUDE_FIVE_LEVELS);
  }
  if (/^claude-(?:opus-4-6|sonnet-4-6)(?:$|-)/.test(modelId)) {
    return toOptions(CLAUDE_FOUR_LEVELS);
  }

  if (
    /^gemini-(?:3\.8-flash|3\.1-pro|2\.5-(?:pro|flash))(?:$|-)/.test(modelId)
  ) {
    return toOptions(THREE_LEVELS);
  }
  if (/^gemini-3-flash(?:$|-)/.test(modelId)) return toOptions(GEMINI_FOUR_LEVELS);

  if (/^deepseek-v4(?:$|-)/.test(modelId)) {
    return toOptions(['low', 'high', 'max']);
  }
  if (/^openai\/gpt-oss-(?:20b|120b)(?:$|-)/.test(modelId)) {
    return toOptions(THREE_LEVELS);
  }

  return [];
};

export const getReasoningLevelLabel = (value) => {
  if (!value) return 'Auto';
  const normalizedValue = String(value).trim().toLowerCase();
  return LEVEL_DETAILS[normalizedValue]?.label || value;
};

export const isReasoningLevelSupported = (value, options = []) => {
  if (!value) return true;
  const normalizedValue = String(value).trim().toLowerCase();
  return options.some((option) => option.value === normalizedValue);
};
